import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import Select from "react-select";
import DatePicker from "react-datepicker";
import {
  API_PROJECT_CREATE_NOTIFICATION,
  API_PROJECT_COMPLETE_NOTIFICATION,
  API_PROJECT_CLOSED_NOTIFICATION,
  API_PROJECT_HOLD_NOTIFICATION,
} from "../../config/Endpoints";
import { ThreeDots } from "react-loader-spinner";
import { formatToDateOnly } from "../../utils/helpers";

const TeamMembersNotificationModal = ({
  projectId,
  iscreatedPush,
  onStatusUpdate,
  disabled,
  project,
}) => {
  const accessToken = Cookies.get("accessToken");
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPushStatus, setCurrentPushStatus] = useState(iscreatedPush);
  const [selectedDate, setSelectedDate] = useState(null);

  const [formData, setFormData] = useState({
    reason: null,
  });

  const reasonOptions =
    currentPushStatus === 0
      ? [
          { value: "Project Delivered", label: "Project Delivered" },
          { value: "Project Closed", label: "Project Closed" },
          { value: "Project Created", label: "Project Created" },
          { value: "Project On Hold", label: "Project On Hold" },
        ]
      : currentPushStatus === 1
        ? [
            { value: "Project Delivered", label: "Project Delivered" },
            { value: "Project Closed", label: "Project Closed" },
            { value: "Project On Hold", label: "Project On Hold" },
          ]
        : currentPushStatus === 2
          ? [
              { value: "Project Delivered", label: "Project Delivered" },
              { value: "Project Closed", label: "Project Closed" },
            ]
          : [
              { value: "Project Delivered", label: "Project Delivered" },
              { value: "Project Closed", label: "Project Closed" },
            ];

  const openModal = () => setShowModal(true);
  const closeModal = () => {
    setShowModal(false);
    setShowConfirm(false);
    setFormData({ reason: null });
    document.body.classList.remove("modal-open");
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSend = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const isProjectDelivered = formData.reason.value === "Project Delivered";
      const isProjectClosed = formData.reason.value === "Project Closed";
      const isProjectOnHold = formData.reason.value === "Project On Hold";

      const url = isProjectDelivered
        ? `${API_PROJECT_COMPLETE_NOTIFICATION}/${projectId}`
        : isProjectClosed
          ? `${API_PROJECT_CLOSED_NOTIFICATION}/${projectId}`
          : isProjectOnHold
            ? `${API_PROJECT_HOLD_NOTIFICATION}/${projectId}/${formatToDateOnly(
                selectedDate,
              )}`
            : `${API_PROJECT_CREATE_NOTIFICATION}/${projectId}`;

      const res = await axios.post(url, null, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      toast.success(
        res.data?.message || `Notification sent for ${formData.reason.value}`,
      );

      if (isProjectDelivered) {
        onStatusUpdate(projectId, 1);
      } else if (isProjectClosed) {
        onStatusUpdate(projectId, 3);
      } else if (formData.reason.value === "Project Created") {
        setCurrentPushStatus(1);
        onStatusUpdate(projectId, 0, 1);
      } else if (formData.reason.value === "Project On Hold") {
        setCurrentPushStatus(2);
        onStatusUpdate(projectId, 2, 2);
      }
      setShowConfirm(false);
      closeModal();
      document.body.classList.remove("modal-open");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal || showConfirm) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [showModal, showConfirm]);

  return (
    <>
      <button
        className="btn theme-button"
        style={{ backgroundColor: "#d63737ff" }}
        onClick={openModal}
        disabled={disabled}
      >
        Push Notification
      </button>

      {/* Main Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content rounded-lg">
              <div className="modal-header">
                <h5 className="modal-title">Send Notification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  style={{ filter: "invert(0.3)" }}
                ></button>
              </div>

              <form>
                <div
                  className="modal-body py-4 px-4"
                  style={{ backgroundColor: "#fafbff" }}
                >
                  <div className="container-fluid">
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3 text-end">
                        <label
                          className="form-label mb-0 has-asterisk"
                          style={{ color: "#333" }}
                        >
                          Reason
                        </label>
                      </div>
                      <div className="col-md-8">
                        <Select
                          options={reasonOptions}
                          className="custom-select"
                          classNamePrefix="react-select"
                          value={formData.reason}
                          onChange={(option) => handleChange("reason", option)}
                          placeholder="Select Reason"
                          required
                        />
                      </div>
                    </div>
                    {formData.reason?.value === "Project On Hold" && (
                      <div className="row mb-3 align-items-center">
                        <div className="col-md-3 text-end">
                          <label
                            className="form-label mb-0 has-asterisk"
                            style={{ color: "#333" }}
                          >
                            Date
                          </label>
                        </div>
                        <div className="col-md-8">
                          <DatePicker
                            showIcon
                            toggleCalendarOnIconClick
                            placeholderText="mm/dd/yyyy"
                            selected={selectedDate}
                            className="border custom-date"
                            onChange={(date) => setSelectedDate(date)}
                            minDate={
                              project.startDate
                                ? new Date(project.startDate)
                                : project.sowStartDate
                                  ? new Date(project.sowStartDate)
                                  : null
                            }
                            maxDate={
                              project.endDate
                                ? new Date(
                                    Math.min(
                                      new Date(project.endDate),
                                      new Date(),
                                    ),
                                  )
                                : project.sowEndDate
                                  ? new Date(
                                      Math.min(
                                        new Date(project.sowEndDate),
                                        new Date(),
                                      ),
                                    )
                                  : new Date()
                            }
                            required
                            onKeyDown={(e) => {
                              if (e.key !== "Backspace" && e.key !== "Delete") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="modal-footer gap-2 rounded-lg"
                  style={{
                    backgroundColor: "#f8f9ff",
                    borderTop: "1px solid #e1e5f7",
                  }}
                >
                  <button
                    type="button"
                    className="theme-button close-modal-btn"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="theme-button notification-send-btn "
                    disabled={
                      !formData.reason ||
                      (formData.reason?.value === "Project On Hold" &&
                        !selectedDate)
                    }
                    onClick={handleSend}
                  >
                    Send
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content rounded-lg">
              <div className="modal-body text-center py-4">
                <h5>Are you sure you want to send this notification?</h5>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn theme-button"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <ThreeDots
                      height="20"
                      width="53"
                      radius="9"
                      color="white"
                      visible={true}
                    />
                  ) : (
                    "Yes"
                  )}
                </button>
                <button
                  type="button"
                  className="btn theme-button bg-grey mx-3"
                  onClick={() => setShowConfirm(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamMembersNotificationModal;
